<template>
  <section class="tab-pane">
    <section class="detailed-analysis">
      <section v-if="hasIssues" class="analysis-section">
        <header class="section-header">
          <h4>
            <i class="fas fa-exclamation-circle"></i>
            {{ t('relatorio_problemas_identificados') }}
            <span class="counter-badge">{{ filteredIssues.length }}</span>
          </h4>
        </header>
        <section class="highlight-legend-toolbar">
          <div class="highlight-legend">
            <span class="legend-title">{{ t('relatorio_legenda_highlight') }}:</span>
            <span class="legend-item legend-usage">{{ t('relatorio_legenda_utilizacao') }}</span>
            <span class="legend-item legend-candidate">{{ t('relatorio_legenda_oportunidade') }}</span>
            <span class="legend-item legend-conflict">{{ t('relatorio_legenda_conflito') }}</span>
          </div>
          <button
            type="button"
            class="clear-highlights-btn"
            @click="$emit('clear-highlights-request')"
          >
            {{ t('relatorio_limpar_destaques') }}
          </button>
        </section>

        <section class="items-list">
          <article
            v-for="(issue, issueIndex) in filteredIssues"
            :key="getIssueKey(issue, issueIndex)"
            :data-issue-key="getIssueKey(issue, issueIndex)"
            class="list-item"
            :class="getDictionary(issue.severity, 'severity', 'class')"
          >
            <button
              v-if="salvarOk"
              type="button"
              class="ignore-issue-btn"
              @click.stop="adjustProblemaIgnorado(issue)"
              :title="problemaIgnorado ? 'Reativar problema' : 'Ignorar problema'"
            >
              -
            </button>
            <button
              type="button"
              class="issue-accordion-header"
              @click="toggleIssue(getIssueKey(issue, issueIndex), issue, issueIndex)"
            >
              <div class="item-header">
                <div
                  class="item-icon"
                  :class="getDictionary(issue.severity, 'severity', 'class')"
                >
                  <i class="fas fa-fw" :class="getDictionary(issue.severity, 'severity', 'icon')"></i>
                </div>
                <div class="item-content">
                  <div class="item-title">{{ issue.title }}</div>
                  <div class="item-description">{{ getIssueDescription(issue, t) }}</div>
                </div>
              </div>
              <div class="issue-header-right">
                <span v-if="hasEntities(issue)" class="entity-badge">
                  {{ getEntitiesCount(issue) }}
                </span>
                <span
                  class="accordion-chevron"
                  :key="`issue-chevron-${getIssueKey(issue, issueIndex)}-${isIssueExpanded(getIssueKey(issue, issueIndex)) ? 'open' : 'closed'}`"
                  :title="isIssueExpanded(getIssueKey(issue, issueIndex)) ? t('relatorio_recolher') : t('relatorio_expandir')"
                  :aria-label="isIssueExpanded(getIssueKey(issue, issueIndex)) ? t('relatorio_recolher') : t('relatorio_expandir')"
                >
                  <i
                    class="fas"
                    :class="isIssueExpanded(getIssueKey(issue, issueIndex)) ? 'fa-chevron-down' : 'fa-chevron-right'"
                  ></i>
                </span>
              </div>
            </button>

            <!-- Corpo do accordion -->
            <section
              v-if="isIssueExpanded(getIssueKey(issue, issueIndex))"
              class="issue-accordion-body"
            >
              <!-- R3 -->
              <section
                v-if="isR3Issue(issue) && hasR3GroupedEntities(issue)"
                class="entity-list"
              >
                <article
                  v-for="group in getR3EntityGroups(issue).filter(g => g.entities.some(e => entityMatchesAba(issue, e)))"
                  :key="`${getIssueKey(issue, issueIndex)}-r3-group-${group.groupKey}`"
                  class="entity-card entity-card-collapsible"
                >
                  <button
                    type="button"
                    class="entity-accordion-header"
                    @click="toggleR3Group(issue, issueIndex, group.groupKey)"
                  >
                    <div class="entity-accordion-title">{{ group.label }}</div>
                    <div class="entity-accordion-right">
                      <button
                        v-if="salvarOk"
                        type="button"
                        class="ignore-issue-btn"
                        @click.stop="adjustGroupingEntitiesIgnorado(issue, issueIndex, group.entities.filter(e => entityMatchesAba(issue, e)))"
                        :title="problemaIgnorado ? 'Reativar problema' : 'Ignorar problema'"
                      >
                        -
                      </button>
                      <span class="entity-badge">{{ group.entities.filter(e => entityMatchesAba(issue, e)).length }}</span>
                      <span
                        class="accordion-chevron"
                        :key="`r3-chevron-${getR3GroupStorageKey(issue, issueIndex, group.groupKey)}-${isR3GroupExpanded(issue, issueIndex, group.groupKey) ? 'open' : 'closed'}`"
                      >
                        <i
                          class="fas"
                          :class="isR3GroupExpanded(issue, issueIndex, group.groupKey) ? 'fa-chevron-down' : 'fa-chevron-right'"
                        ></i>
                      </span>
                    </div>
                  </button>

                  <section
                    v-if="isR3GroupExpanded(issue, issueIndex, group.groupKey)"
                    class="entity-card-body"
                  >
                    <section class="r3-group-entities">
                      <article
                        v-for="(entity, entityIndex) in group.entities.filter(e => entityMatchesAba(issue, e))"
                        :key="`${getIssueKey(issue, issueIndex)}-${group.groupKey}-entity-${entityIndex}`"
                        class="entity-card r3-entity-card"
                      >
                        <div v-if="salvarOk" class="entity-card-toolbar">
                          <button
                            type="button"
                            class="ignore-issue-btn"
                            @click.stop="adjustEntityIgnorado(issue, issueIndex, entity, getRealEntityIndex(issue, entity))"
                            :title="problemaIgnorado ? 'Reativar problema' : 'Ignorar problema'"
                          >
                            -
                          </button>
                        </div>
                        <section class="entity-card-body entity-display-r3-repetition">
                          <div class="entity-grid r3-summary-grid">
                            <div v-if="isR3ByTypeEntity(entity)" class="entity-line">
                              <strong>{{ t('relatorio_entidade_tipo') }}:</strong>
                              <span>{{ entity.tipo || '-' }}</span>
                            </div>
                            <div class="entity-line">
                              <strong>{{ t('date') }}:</strong>
                              <span>{{ formatDate(entity.date) }}</span>
                            </div>
                          </div>

                          <div v-if="buildR3Tags(entity, t).length || getR3EntityActions(entity, t).length" class="r3-tags-actions-row">
                            <div v-if="buildR3Tags(entity, t).length" class="entity-tags r3-tags-row">
                              <template v-for="(tag, tagIndex) in buildR3Tags(entity, t)">
                                <button
                                  v-if="isActionableTag(tag)"
                                  type="button"
                                  class="entity-tag entity-tag-action"
                                  :key="`${issueIndex}-${group.groupKey}-${entityIndex}-tag-btn-${tagIndex}`"
                                  @click.stop="handleTagAction(tag, issue, entity)"
                                >
                                  {{ getTagLabel(tag, t) }}
                                </button>
                                <span
                                  v-else
                                  class="entity-tag"
                                  :key="`${issueIndex}-${group.groupKey}-${entityIndex}-tag-txt-${tagIndex}`"
                                >
                                  {{ getTagLabel(tag, t) }}
                                </span>
                              </template>
                            </div>

                            <div v-if="getR3EntityActions(entity, t).length" class="entity-actions r3-actions-row">
                              <button
                                v-for="(action, actionIndex) in getR3EntityActions(entity, t)"
                                :key="`${issueIndex}-${group.groupKey}-${entityIndex}-action-${actionIndex}`"
                                type="button"
                                class="navigate-btn"
                                :class="getActionButtonClass(action)"
                                @click="handleEntityAction(action, issue, entity)"
                              >
                                {{ getActionLabel(action, t) }}
                              </button>
                            </div>
                          </div>
                        </section>
                      </article>
                    </section>
                  </section>
                </article>
              </section>

              <!-- R8 -->
              <section
                v-else-if="isR8Issue(issue) && hasR8GroupedEntities(issue)"
                class="entity-list"
              >
                <article
                  v-for="group in getR8EntityGroups(issue).filter(g => g.entities.some(e => entityMatchesAba(issue, e)))"
                  :key="`${getIssueKey(issue, issueIndex)}-r8-group-${group.groupKey}`"
                  class="entity-card entity-card-collapsible"
                >
                  <button
                    type="button"
                    class="entity-accordion-header"
                    @click="toggleR8Group(issue, issueIndex, group.groupKey)"
                  >
                    <div class="entity-accordion-title">{{ group.label }}</div>
                    <div class="entity-accordion-right">
                      <button
                        v-if="salvarOk"
                        type="button"
                        class="ignore-issue-btn"
                        @click.stop="adjustGroupingEntitiesIgnorado(issue, issueIndex, group.entities.filter(e => entityMatchesAba(issue, e)))"
                        :title="problemaIgnorado ? 'Reativar problema' : 'Ignorar problema'"
                      >
                        -
                      </button>
                      <span class="entity-badge">{{ group.entities.filter(e => entityMatchesAba(issue, e)).length }}</span>
                      <span
                        class="accordion-chevron"
                        :key="`r8-chevron-${getR8GroupStorageKey(issue, issueIndex, group.groupKey)}-${isR8GroupExpanded(issue, issueIndex, group.groupKey) ? 'open' : 'closed'}`"
                      >
                        <i
                          class="fas"
                          :class="isR8GroupExpanded(issue, issueIndex, group.groupKey) ? 'fa-chevron-down' : 'fa-chevron-right'"
                        ></i>
                      </span>
                    </div>
                  </button>

                  <section
                    v-if="isR8GroupExpanded(issue, issueIndex, group.groupKey)"
                    class="entity-card-body"
                  >
                    <section class="r8-group-entities">
                      <article
                        v-for="(entity, entityIndex) in group.entities.filter(e => entityMatchesAba(issue, e))"
                        :key="`${getIssueKey(issue, issueIndex)}-${group.groupKey}-entity-${entityIndex}`"
                        class="entity-card r8-entity-card"
                      >
                        <div v-if="salvarOk" class="entity-card-toolbar">
                          <button
                            type="button"
                            class="ignore-issue-btn"
                            @click.stop="adjustEntityIgnorado(issue, issueIndex, entity, getRealEntityIndex(issue, entity))"
                            :title="problemaIgnorado ? 'Reativar problema' : 'Ignorar problema'"
                          >
                            -
                          </button>
                        </div>
                        <section class="entity-card-body">
                          <!-- Fallback ou view -->
                          <div v-if="getEntityView(issue, entity, t).fallback" class="entity-fallback">
                            <p>{{ t('relatorio_entidade_fallback') }}</p>
                            <pre>{{ stringifyEntity(entity) }}</pre>
                          </div>

                          <div
                            v-else-if="isR3Entity(issue, entity)"
                            class="entity-display-r3-repetition"
                          >
                            <!-- Exibição R3 simplificada (já tratada acima, mas pode ocorrer aqui também) -->
                            <div class="entity-grid r3-summary-grid">
                              <div
                                v-for="(row, rowIndex) in getEntityView(issue, entity, t).rows"
                                :key="`${issueIndex}-${group.groupKey}-${entityIndex}-r3-row-${rowIndex}`"
                                class="entity-line"
                                :class="{
                                  'entity-line-usage': row.type === 'usage',
                                  'entity-line-entity': row.type === 'entity',
                                  'entity-line-single-line': row.singleLine
                                }"
                              >
                                <strong>{{ row.label }}:</strong>
                                <template v-if="row.type === 'entity'">
                                  <div class="entity-value-entity" :class="{ 'entity-value-entity-single-line': row.singleLine }">
                                    <span v-if="row.code" class="entity-code-chip" :title="row.code">{{ row.code }}</span>
                                    <span class="entity-value-name" :class="{ 'entity-value-name-single-line': row.singleLine }" :title="row.nameTitle || row.name || '-'">{{ row.name || '-' }}</span>
                                  </div>
                                </template>
                                <template v-else-if="row.type === 'usage'">
                                  <div class="entity-usage-metrics">
                                    <span class="group-usage-chip">{{ t('relatorio_utilizado') }}: {{ row.usage.used }}</span>
                                    <span class="group-usage-chip">{{ t('relatorio_minimo') }}: {{ row.usage.min }}</span>
                                    <span class="group-usage-chip">{{ t('relatorio_maximo') }}: {{ row.usage.max }}</span>
                                    <span v-if="row.usage.delta !== null" class="group-usage-chip">{{ t('relatorio_diferenca') }}: {{ row.usage.delta }}</span>
                                  </div>
                                </template>
                                <template v-else>
                                  <span :class="{ 'entity-text-value-single-line': row.singleLine }" :title="row.valueTitle || row.value || '-'">{{ row.value }}</span>
                                </template>
                              </div>
                            </div>

                            <div v-if="getEntityView(issue, entity, t).tags.length || getEntityView(issue, entity, t).actions.length" class="r3-tags-actions-row">
                              <div v-if="getEntityView(issue, entity, t).tags.length" class="entity-tags r3-tags-row">
                                <template v-for="(tag, tagIndex) in getEntityView(issue, entity, t).tags">
                                  <button
                                    v-if="isActionableTag(tag)"
                                    type="button"
                                    class="entity-tag entity-tag-action"
                                    :key="`${issueIndex}-${group.groupKey}-${entityIndex}-r3-tag-btn-${tagIndex}`"
                                    @click.stop="handleTagAction(tag, issue, entity)"
                                  >
                                    {{ getTagLabel(tag, t) }}
                                  </button>
                                  <span v-else class="entity-tag" :key="`${issueIndex}-${group.groupKey}-${entityIndex}-r3-tag-txt-${tagIndex}`">
                                    {{ getTagLabel(tag, t) }}
                                  </span>
                                </template>
                              </div>

                              <div v-if="getEntityView(issue, entity, t).actions.length" class="entity-actions r3-actions-row">
                                <button
                                  v-for="(action, actionIndex) in getEntityView(issue, entity, t).actions"
                                  :key="`${issueIndex}-${group.groupKey}-${entityIndex}-r3-action-${actionIndex}`"
                                  type="button"
                                  class="navigate-btn"
                                  :class="getActionButtonClass(action)"
                                  @click="handleEntityAction(action, issue, entity)"
                                >
                                  {{ getActionLabel(action, t) }}
                                </button>
                              </div>
                            </div>
                          </div>

                          <!-- Outras regras (R1, R2, R4, R5, R6, R7, R9, R10, R11) -->
                          <div
                            v-else
                            :class="[
                              'entity-display',
                              { 
                                'entity-display-r1-frequency': isR1Entity(issue, entity),
                                'entity-display-r3-repetition': isR3Entity(issue, entity),
                                'entity-display-r8-unmet': isR8UnmetEntity(issue, entity),
                                'entity-display-r10-gap': isR10Entity(issue, entity),
                                'entity-display-r9-conflict': isR9Entity(issue, entity),
                                'entity-display-r11-cost': isR11Entity(issue, entity)
                              }
                            ]"
                          >
                            <div class="entity-grid">
                              <div
                                v-for="(row, rowIndex) in getEntityView(issue, entity, t).rows"
                                :key="`${issueIndex}-${group.groupKey}-${entityIndex}-row-${rowIndex}`"
                                class="entity-line"
                                :class="{
                                  'entity-line-usage': row.type === 'usage',
                                  'entity-line-entity': row.type === 'entity',
                                  'entity-line-single-line': row.singleLine
                                }"
                              >
                                <strong>{{ row.label }}:</strong>
                                <template v-if="row.type === 'entity'">
                                  <div class="entity-value-entity" :class="{ 'entity-value-entity-single-line': row.singleLine }">
                                    <span v-if="row.code" class="entity-code-chip" :title="row.code">{{ row.code }}</span>
                                    <span class="entity-value-name" :class="{ 'entity-value-name-single-line': row.singleLine }" :title="row.nameTitle || row.name || '-'">{{ row.name || '-' }}</span>
                                  </div>
                                </template>
                                <template v-else-if="row.type === 'usage'">
                                  <div class="entity-usage-metrics">
                                    <span class="group-usage-chip">{{ t('relatorio_utilizado') }}: {{ row.usage.used }}</span>
                                    <span class="group-usage-chip">{{ t('relatorio_minimo') }}: {{ row.usage.min }}</span>
                                    <span class="group-usage-chip">{{ t('relatorio_maximo') }}: {{ row.usage.max }}</span>
                                    <span v-if="row.usage.delta !== null" class="group-usage-chip">{{ t('relatorio_diferenca') }}: {{ row.usage.delta }}</span>
                                  </div>
                                </template>
                                <template v-else>
                                  <span :class="{ 'entity-text-value-single-line': row.singleLine }" :title="row.valueTitle || row.value || '-'">{{ row.value }}</span>
                                </template>
                              </div>
                            </div>

                            <div v-if="getEntityView(issue, entity, t).tags.length" class="entity-tags">
                              <template v-for="(tag, tagIndex) in getEntityView(issue, entity, t).tags">
                                <button
                                  v-if="isActionableTag(tag)"
                                  type="button"
                                  class="entity-tag entity-tag-action"
                                  :key="`${issueIndex}-${group.groupKey}-${entityIndex}-tag-btn-${tagIndex}`"
                                  @click.stop="handleTagAction(tag, issue, entity)"
                                >
                                  {{ getTagLabel(tag, t) }}
                                </button>
                                <span v-else class="entity-tag" :key="`${issueIndex}-${group.groupKey}-${entityIndex}-tag-txt-${tagIndex}`">
                                  {{ getTagLabel(tag, t) }}
                                </span>
                              </template>
                            </div>

                            <div v-if="getEntityView(issue, entity, t).lines.length" class="entity-lines">
                              <div
                                v-for="(line, lineIndex) in getEntityView(issue, entity, t).lines"
                                :key="`${issueIndex}-${group.groupKey}-${entityIndex}-line-${lineIndex}`"
                                class="entity-line"
                                :class="{
                                  'entity-line-group-usage': line.type === 'group-usage',
                                  'entity-line-r5-details': line.type === 'r5-details',
                                  'entity-line-r9-summary': line.type === 'r9-summary',
                                  'entity-line-r9-details': line.type === 'r9-details',
                                  'entity-line-r4-details': line.type === 'r4-details',
                                  'entity-line-r11-plate': line.type === 'r11-plate'
                                }"
                              >
                                <template v-if="line.type === 'group-usage'">
                                  <div class="group-usage-header">
                                    <span class="group-name">{{ line.name || '-' }}</span>
                                  </div>
                                  <div class="group-usage-metrics">
                                    <span class="group-usage-scope">{{ line.scope }}</span>
                                    <span class="group-usage-chip">{{ t('relatorio_utilizado') }}: {{ line.used }}</span>
                                    <span class="group-usage-chip">{{ t('relatorio_minimo') }}: {{ line.min }}</span>
                                    <span class="group-usage-chip">{{ t('relatorio_maximo') }}: {{ line.max }}</span>
                                    <span v-if="line.delta !== null" class="group-usage-chip">{{ t('relatorio_diferenca') }}: {{ line.delta }}</span>
                                  </div>
                                  <div v-if="line.actions && line.actions.length" class="group-usage-actions">
                                    <button
                                      v-for="(lineAction, lineActionIndex) in line.actions"
                                      :key="`${issueIndex}-${group.groupKey}-${entityIndex}-line-${lineIndex}-action-${lineActionIndex}`"
                                      type="button"
                                      class="navigate-btn"
                                      :class="getActionButtonClass(lineAction)"
                                      @click="handleEntityAction(lineAction, issue, entity)"
                                    >
                                      {{ getActionLabel(lineAction, t) }}
                                    </button>
                                  </div>
                                </template>
                                <template v-else-if="line.type === 'occurrence'">
                                  <div class="group-usage-metrics">
                                    <span class="group-usage-chip">{{ t('date') }}: {{ line.date }}</span>
                                    <span class="group-usage-chip">{{ t('relatorio_entidade_coluna') }}: {{ line.sequencial }}</span>
                                  </div>
                                  <button
                                    v-if="line.action"
                                    type="button"
                                    class="navigate-btn"
                                    :class="getActionButtonClass(line.action)"
                                    @click="handleLineAction(line, issue, entity)"
                                  >
                                    {{ getActionLabel(line.action, t) }}
                                  </button>
                                </template>
                                <template v-else-if="line.type === 'r5-details'">
                                  <div class="r5-details-layout">
                                    <div class="r5-details-grid">
                                      <div
                                        v-for="(item, itemIndex) in line.items"
                                        :key="`${issueIndex}-${group.groupKey}-${entityIndex}-line-${lineIndex}-r5-item-${itemIndex}`"
                                        class="entity-line r5-detail-item"
                                      >
                                        <strong>{{ item.label }}:</strong>
                                        <span class="r5-detail-value" :title="item.valueTitle || item.value || '-'">{{ item.value || '-' }}</span>
                                      </div>
                                      <button
                                        v-if="line.action"
                                        type="button"
                                        class="navigate-btn r5-details-action"
                                        :class="getActionButtonClass(line.action)"
                                        @click="handleLineAction(line, issue, entity)"
                                      >
                                        {{ getActionLabel(line.action, t) }}
                                      </button>
                                    </div>
                                  </div>
                                </template>
                                <template v-else-if="line.type === 'r9-summary'">
                                  <div class="r9-summary-grid">
                                    <div
                                      v-for="(item, itemIndex) in line.items"
                                      :key="`${issueIndex}-${group.groupKey}-${entityIndex}-line-${lineIndex}-summary-${itemIndex}`"
                                      class="entity-line r9-summary-item"
                                    >
                                      <strong>{{ item.label }}:</strong>
                                      <span>{{ item.value }}</span>
                                    </div>
                                  </div>
                                </template>
                                <template v-else-if="line.type === 'r9-details'">
                                  <div class="r9-details-grid">
                                    <div
                                      v-for="(item, itemIndex) in line.items"
                                      :key="`${issueIndex}-${group.groupKey}-${entityIndex}-line-${lineIndex}-details-${itemIndex}`"
                                      class="entity-line entity-line-entity entity-line-single-line r9-detail-item"
                                    >
                                      <strong>{{ item.label }}:</strong>
                                      <div class="entity-value-entity entity-value-entity-single-line">
                                        <span v-if="item.code" class="entity-code-chip" :title="item.code">{{ item.code }}</span>
                                        <span class="entity-value-name entity-value-name-single-line" :title="item.nameTitle || item.name || '-'">{{ item.name || '-' }}</span>
                                      </div>
                                    </div>
                                    <button
                                      v-if="line.action"
                                      type="button"
                                      class="navigate-btn r9-details-action"
                                      :class="getActionButtonClass(line.action)"
                                      @click="handleLineAction(line, issue, entity)"
                                    >
                                      {{ getActionLabel(line.action, t) }}
                                    </button>
                                  </div>
                                </template>
                                <template v-else-if="line.type === 'r4-details'">
                                  <div class="r4-details-grid">
                                    <div
                                      v-for="(item, itemIndex) in line.items"
                                      :key="`${issueIndex}-${group.groupKey}-${entityIndex}-line-${lineIndex}-r4-${itemIndex}`"
                                      :class="[
                                        'entity-line',
                                        'r4-detail-item',
                                        { 'entity-line-entity entity-line-single-line': item.type === 'entity' }
                                      ]"
                                    >
                                      <strong>{{ item.label }}:</strong>
                                      <template v-if="item.type === 'entity'">
                                        <div class="entity-value-entity entity-value-entity-single-line">
                                          <span v-if="item.code" class="entity-code-chip" :title="item.code">{{ item.code }}</span>
                                          <span class="entity-value-name entity-value-name-single-line" :title="item.nameTitle || item.name || '-'">{{ item.name || '-' }}</span>
                                        </div>
                                      </template>
                                      <template v-else>
                                        <span class="r4-detail-value" :title="item.valueTitle || item.value || '-'">{{ item.value || '-' }}</span>
                                      </template>
                                    </div>
                                    <button
                                      v-if="line.action"
                                      type="button"
                                      class="navigate-btn r4-details-action"
                                      :class="getActionButtonClass(line.action)"
                                      @click="handleLineAction(line, issue, entity)"
                                    >
                                      {{ getActionLabel(line.action, t) }}
                                    </button>
                                  </div>
                                </template>
                                <template v-else-if="line.type === 'r11-plate'">
                                  <div class="r11-plate-grid">
                                    <div
                                      v-for="(item, itemIndex) in line.items"
                                      :key="`${issueIndex}-${group.groupKey}-${entityIndex}-line-${lineIndex}-r11-${itemIndex}`"
                                      :class="[
                                        'entity-line',
                                        'r11-plate-item',
                                        { 'entity-line-entity entity-line-single-line': item.type === 'entity' }
                                      ]"
                                    >
                                      <strong>{{ item.label }}:</strong>
                                      <template v-if="item.type === 'entity'">
                                        <div class="entity-value-entity entity-value-entity-single-line">
                                          <span v-if="item.code" class="entity-code-chip" :title="item.code">{{ item.code }}</span>
                                          <span class="entity-value-name entity-value-name-single-line" :title="item.nameTitle || item.name || '-'">{{ item.name || '-' }}</span>
                                        </div>
                                      </template>
                                      <template v-else>
                                        <span class="r11-plate-value" :title="item.valueTitle || item.value || '-'">{{ item.value || '-' }}</span>
                                      </template>
                                    </div>
                                    <button
                                      v-if="line.action"
                                      type="button"
                                      class="navigate-btn r11-plate-action"
                                      :class="getActionButtonClass(line.action)"
                                      @click="handleLineAction(line, issue, entity)"
                                    >
                                      {{ getActionLabel(line.action, t) }}
                                    </button>
                                  </div>
                                </template>
                                <template v-else>
                                  <span>{{ line.text }}</span>
                                  <button
                                    v-if="line.action"
                                    type="button"
                                    class="navigate-btn"
                                    :class="getActionButtonClass(line.action)"
                                    @click="handleLineAction(line, issue, entity)"
                                  >
                                    {{ getActionLabel(line.action, t) }}
                                  </button>
                                </template>
                              </div>
                            </div>

                            <div v-if="getEntityView(issue, entity, t).actions.length" class="entity-actions">
                              <button
                                v-for="(action, actionIndex) in getEntityView(issue, entity, t).actions"
                                :key="`${issueIndex}-${group.groupKey}-${entityIndex}-action-${actionIndex}`"
                                type="button"
                                class="navigate-btn"
                                :class="getActionButtonClass(action)"
                                @click="handleEntityAction(action, issue, entity)"
                              >
                                {{ getActionLabel(action, t) }}
                              </button>
                            </div>
                          </div>
                        </section>
                      </article>
                    </section>
                  </section>
                </article>
              </section>

              <!-- Demais entidades (não agrupadas) -->
              <div v-else-if="hasEntities(issue)" class="entity-list">
                <div
                  v-for="{ entity, entityIndex } in visibleEntitiesForAba(issue)"
                  :key="`${getIssueKey(issue, issueIndex)}-entity-${entityIndex}`"
                  class="entity-card"
                  :class="{ 'entity-card-collapsible': isR2Entity(issue, entity) }"
                >
                  <button
                    v-if="isR2Entity(issue, entity)"
                    type="button"
                    class="entity-accordion-header"
                    @click="toggleR2Entity(issue, issueIndex, entity, entityIndex)"
                  >
                    <div class="entity-accordion-title">{{ formatCategory(entity.category) }}</div>
                    <div class="entity-accordion-right">
                      <button
                        v-if="salvarOk"
                        type="button"
                        class="ignore-issue-btn"
                        @click.stop="adjustEntityIgnorado(issue, issueIndex, entity, entityIndex)"
                        :title="problemaIgnorado ? 'Reativar problema' : 'Ignorar problema'"
                      >
                        -
                      </button>
                      <span class="entity-badge">{{ entity.count || 0 }}</span>
                      <span
                        class="accordion-chevron"
                        :key="`r2-chevron-${getR2EntityKey(issue, issueIndex, entity, entityIndex)}-${isR2EntityExpanded(issue, issueIndex, entity, entityIndex) ? 'open' : 'closed'}`"
                      >
                        <i
                          class="fas"
                          :class="isR2EntityExpanded(issue, issueIndex, entity, entityIndex) ? 'fa-chevron-down' : 'fa-chevron-right'"
                        ></i>
                      </span>
                    </div>
                  </button>

                  <div
                    v-if="salvarOk && !isR2Entity(issue, entity)"
                    class="entity-card-toolbar"
                  >
                    <button
                      type="button"
                      class="ignore-issue-btn"
                      @click.stop="adjustEntityIgnorado(issue, issueIndex, entity, entityIndex)"
                      :title="problemaIgnorado ? 'Reativar problema' : 'Ignorar problema'"
                    >
                      -
                    </button>
                  </div>

                  <div
                    v-if="!isR2Entity(issue, entity) || isR2EntityExpanded(issue, issueIndex, entity, entityIndex)"
                    class="entity-card-body"
                  >
                    <!-- Fallback -->
                    <div v-if="getEntityView(issue, entity, t).fallback" class="entity-fallback">
                      <p>{{ t('relatorio_entidade_fallback') }}</p>
                      <pre>{{ stringifyEntity(entity) }}</pre>
                    </div>

                    <!-- R3 -->
                    <div v-else-if="isR3Entity(issue, entity)" class="entity-display-r3-repetition">
                      <div class="entity-grid r3-summary-grid">
                        <div
                          v-for="(row, rowIndex) in getEntityView(issue, entity, t).rows"
                          :key="`${issueIndex}-${entityIndex}-r3-row-${rowIndex}`"
                          class="entity-line"
                          :class="{
                            'entity-line-usage': row.type === 'usage',
                            'entity-line-entity': row.type === 'entity',
                            'entity-line-single-line': row.singleLine
                          }"
                        >
                          <strong>{{ row.label }}:</strong>
                          <template v-if="row.type === 'entity'">
                            <div class="entity-value-entity" :class="{ 'entity-value-entity-single-line': row.singleLine }">
                              <span v-if="row.code" class="entity-code-chip" :title="row.code">{{ row.code }}</span>
                              <span class="entity-value-name" :class="{ 'entity-value-name-single-line': row.singleLine }" :title="row.nameTitle || row.name || '-'">{{ row.name || '-' }}</span>
                            </div>
                          </template>
                          <template v-else-if="row.type === 'usage'">
                            <div class="entity-usage-metrics">
                              <span class="group-usage-chip">{{ t('relatorio_utilizado') }}: {{ row.usage.used }}</span>
                              <span class="group-usage-chip">{{ t('relatorio_minimo') }}: {{ row.usage.min }}</span>
                              <span class="group-usage-chip">{{ t('relatorio_maximo') }}: {{ row.usage.max }}</span>
                              <span v-if="row.usage.delta !== null" class="group-usage-chip">{{ t('relatorio_diferenca') }}: {{ row.usage.delta }}</span>
                            </div>
                          </template>
                          <template v-else>
                            <span :class="{ 'entity-text-value-single-line': row.singleLine }" :title="row.valueTitle || row.value || '-'">{{ row.value }}</span>
                          </template>
                        </div>
                      </div>

                      <div v-if="getEntityView(issue, entity, t).tags.length || getEntityView(issue, entity, t).actions.length" class="r3-tags-actions-row">
                        <div v-if="getEntityView(issue, entity, t).tags.length" class="entity-tags r3-tags-row">
                          <template v-for="(tag, tagIndex) in getEntityView(issue, entity, t).tags">
                            <button
                              v-if="isActionableTag(tag)"
                              type="button"
                              class="entity-tag entity-tag-action"
                              :key="`${issueIndex}-${entityIndex}-r3-tag-btn-${tagIndex}`"
                              @click.stop="handleTagAction(tag, issue, entity)"
                            >
                              {{ getTagLabel(tag, t) }}
                            </button>
                            <span v-else class="entity-tag" :key="`${issueIndex}-${entityIndex}-r3-tag-txt-${tagIndex}`">
                              {{ getTagLabel(tag, t) }}
                            </span>
                          </template>
                        </div>

                        <div v-if="getEntityView(issue, entity, t).actions.length" class="entity-actions r3-actions-row">
                          <button
                            v-for="(action, actionIndex) in getEntityView(issue, entity, t).actions"
                            :key="`${issueIndex}-${entityIndex}-r3-action-${actionIndex}`"
                            type="button"
                            class="navigate-btn"
                            :class="getActionButtonClass(action)"
                            @click="handleEntityAction(action, issue, entity)"
                          >
                            {{ getActionLabel(action, t) }}
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Outras regras -->
                    <div
                      v-else
                      :class="{
                        'entity-display-r1-frequency': isR1Entity(issue, entity),
                        'entity-display-r3-repetition': isR3Entity(issue, entity),
                        'entity-display-r8-unmet': isR8UnmetEntity(issue, entity),
                        'entity-display-r10-gap': isR10Entity(issue, entity),
                        'entity-display-r9-conflict': isR9Entity(issue, entity),
                        'entity-display-r11-cost': isR11Entity(issue, entity)
                      }"
                    >
                      <div class="entity-grid">
                        <div
                          v-for="(row, rowIndex) in getEntityView(issue, entity, t).rows"
                          :key="`${issueIndex}-${entityIndex}-row-${rowIndex}`"
                          class="entity-line"
                          :class="{
                            'entity-line-usage': row.type === 'usage',
                            'entity-line-entity': row.type === 'entity',
                            'entity-line-single-line': row.singleLine
                          }"
                        >
                          <strong>{{ row.label }}:</strong>
                          <template v-if="row.type === 'entity'">
                            <div class="entity-value-entity" :class="{ 'entity-value-entity-single-line': row.singleLine }">
                              <span v-if="row.code" class="entity-code-chip" :title="row.code">{{ row.code }}</span>
                              <span class="entity-value-name" :class="{ 'entity-value-name-single-line': row.singleLine }" :title="row.nameTitle || row.name || '-'">{{ row.name || '-' }}</span>
                            </div>
                          </template>
                          <template v-else-if="row.type === 'usage'">
                            <div class="entity-usage-metrics">
                              <span class="group-usage-chip">{{ t('relatorio_utilizado') }}: {{ row.usage.used }}</span>
                              <span class="group-usage-chip">{{ t('relatorio_minimo') }}: {{ row.usage.min }}</span>
                              <span class="group-usage-chip">{{ t('relatorio_maximo') }}: {{ row.usage.max }}</span>
                              <span v-if="row.usage.delta !== null" class="group-usage-chip">{{ t('relatorio_diferenca') }}: {{ row.usage.delta }}</span>
                            </div>
                          </template>
                          <template v-else>
                            <span :class="{ 'entity-text-value-single-line': row.singleLine }" :title="row.valueTitle || row.value || '-'">{{ row.value }}</span>
                          </template>
                        </div>
                      </div>

                      <div v-if="getEntityView(issue, entity, t).tags.length" class="entity-tags">
                        <template v-for="(tag, tagIndex) in getEntityView(issue, entity, t).tags">
                          <button
                            v-if="isActionableTag(tag)"
                            type="button"
                            class="entity-tag entity-tag-action"
                            :key="`${issueIndex}-${entityIndex}-tag-btn-${tagIndex}`"
                            @click.stop="handleTagAction(tag, issue, entity)"
                          >
                            {{ getTagLabel(tag, t) }}
                          </button>
                          <span v-else class="entity-tag" :key="`${issueIndex}-${entityIndex}-tag-txt-${tagIndex}`">
                            {{ getTagLabel(tag, t) }}
                          </span>
                        </template>
                      </div>

                      <div v-if="getEntityView(issue, entity, t).lines.length" class="entity-lines">
                        <div
                          v-for="(line, lineIndex) in visibleR2LinesForAba(issue, entity, t)"
                          :key="`${issueIndex}-${entityIndex}-line-${lineIndex}`"
                          class="entity-line"
                          :class="{
                            'entity-line-group-usage': line.type === 'group-usage',
                            'entity-line-r5-details': line.type === 'r5-details',
                            'entity-line-r9-summary': line.type === 'r9-summary',
                            'entity-line-r9-details': line.type === 'r9-details',
                            'entity-line-r4-details': line.type === 'r4-details',
                            'entity-line-r11-plate': line.type === 'r11-plate'
                          }"
                        >
                          <template v-if="line.type === 'group-usage'">
                            <div class="group-usage-header">
                              <span class="group-name">{{ line.name || '-' }}</span>
                              <button
                                v-if="salvarOk"
                                type="button"
                                class="ignore-issue-btn"
                                @click.stop="adjustGroupIgnorado(issue, issueIndex, entity, entityIndex, entityGroupsList(entity)[line.groupIndex], line.groupIndex)"
                                :title="problemaIgnorado ? 'Reativar problema' : 'Ignorar problema'"
                              >
                                -
                              </button>
                            </div>
                            <div class="group-usage-metrics">
                              <span class="group-usage-scope">{{ line.scope }}</span>
                              <span class="group-usage-chip">{{ t('relatorio_utilizado') }}: {{ line.used }}</span>
                              <span class="group-usage-chip">{{ t('relatorio_minimo') }}: {{ line.min }}</span>
                              <span class="group-usage-chip">{{ t('relatorio_maximo') }}: {{ line.max }}</span>
                              <span v-if="line.delta !== null" class="group-usage-chip">{{ t('relatorio_diferenca') }}: {{ line.delta }}</span>
                            </div>
                            <div v-if="line.actions && line.actions.length" class="group-usage-actions">
                              <button
                                v-for="(lineAction, lineActionIndex) in line.actions"
                                :key="`${issueIndex}-${entityIndex}-line-${lineIndex}-action-${lineActionIndex}`"
                                type="button"
                                class="navigate-btn"
                                :class="getActionButtonClass(lineAction)"
                                @click="handleEntityAction(lineAction, issue, entity)"
                              >
                                {{ getActionLabel(lineAction, t) }}
                              </button>
                            </div>
                          </template>
                          <template v-else-if="line.type === 'occurrence'">
                            <div class="group-usage-metrics">
                              <span class="group-usage-chip">{{ t('date') }}: {{ line.date }}</span>
                              <span class="group-usage-chip">{{ t('relatorio_entidade_coluna') }}: {{ line.sequencial }}</span>
                            </div>
                            <button
                              v-if="line.action"
                              type="button"
                              class="navigate-btn"
                              :class="getActionButtonClass(line.action)"
                              @click="handleLineAction(line, issue, entity)"
                            >
                              {{ getActionLabel(line.action, t) }}
                            </button>
                          </template>
                          <template v-else-if="line.type === 'r5-details'">
                            <div class="r5-details-layout">
                              <div class="r5-details-grid">
                                <div
                                  v-for="(item, itemIndex) in line.items"
                                  :key="`${issueIndex}-${entityIndex}-line-${lineIndex}-r5-item-${itemIndex}`"
                                  class="entity-line r5-detail-item"
                                >
                                  <strong>{{ item.label }}:</strong>
                                  <span class="r5-detail-value" :title="item.valueTitle || item.value || '-'">{{ item.value || '-' }}</span>
                                </div>
                                <button
                                  v-if="line.action"
                                  type="button"
                                  class="navigate-btn r5-details-action"
                                  :class="getActionButtonClass(line.action)"
                                  @click="handleLineAction(line, issue, entity)"
                                >
                                  {{ getActionLabel(line.action, t) }}
                                </button>
                              </div>
                            </div>
                          </template>
                          <template v-else-if="line.type === 'r9-summary'">
                            <div class="r9-summary-grid">
                              <div
                                v-for="(item, itemIndex) in line.items"
                                :key="`${issueIndex}-${entityIndex}-line-${lineIndex}-summary-${itemIndex}`"
                                class="entity-line r9-summary-item"
                              >
                                <strong>{{ item.label }}:</strong>
                                <span>{{ item.value }}</span>
                              </div>
                            </div>
                          </template>
                          <template v-else-if="line.type === 'r9-details'">
                            <div class="r9-details-grid">
                              <div
                                v-for="(item, itemIndex) in line.items"
                                :key="`${issueIndex}-${entityIndex}-line-${lineIndex}-details-${itemIndex}`"
                                class="entity-line entity-line-entity entity-line-single-line r9-detail-item"
                              >
                                <strong>{{ item.label }}:</strong>
                                <div class="entity-value-entity entity-value-entity-single-line">
                                  <span v-if="item.code" class="entity-code-chip" :title="item.code">{{ item.code }}</span>
                                  <span class="entity-value-name entity-value-name-single-line" :title="item.nameTitle || item.name || '-'">{{ item.name || '-' }}</span>
                                </div>
                              </div>
                              <button
                                v-if="line.action"
                                type="button"
                                class="navigate-btn r9-details-action"
                                :class="getActionButtonClass(line.action)"
                                @click="handleLineAction(line, issue, entity)"
                              >
                                {{ getActionLabel(line.action, t) }}
                              </button>
                            </div>
                          </template>
                          <template v-else-if="line.type === 'r4-details'">
                            <div class="r4-details-grid">
                              <div
                                v-for="(item, itemIndex) in line.items"
                                :key="`${issueIndex}-${entityIndex}-line-${lineIndex}-r4-${itemIndex}`"
                                :class="[
                                  'entity-line',
                                  'r4-detail-item',
                                  { 'entity-line-entity entity-line-single-line': item.type === 'entity' }
                                ]"
                              >
                                <strong>{{ item.label }}:</strong>
                                <template v-if="item.type === 'entity'">
                                  <div class="entity-value-entity entity-value-entity-single-line">
                                    <span v-if="item.code" class="entity-code-chip" :title="item.code">{{ item.code }}</span>
                                    <span class="entity-value-name entity-value-name-single-line" :title="item.nameTitle || item.name || '-'">{{ item.name || '-' }}</span>
                                  </div>
                                </template>
                                <template v-else>
                                  <span class="r4-detail-value" :title="item.valueTitle || item.value || '-'">{{ item.value || '-' }}</span>
                                </template>
                              </div>
                              <button
                                v-if="line.action"
                                type="button"
                                class="navigate-btn r4-details-action"
                                :class="getActionButtonClass(line.action)"
                                @click="handleLineAction(line, issue, entity)"
                              >
                                {{ getActionLabel(line.action, t) }}
                              </button>
                            </div>
                          </template>
                          <template v-else-if="line.type === 'r11-plate'">
                            <div class="r11-plate-grid">
                              <div
                                v-for="(item, itemIndex) in line.items"
                                :key="`${issueIndex}-${entityIndex}-line-${lineIndex}-r11-${itemIndex}`"
                                :class="[
                                  'entity-line',
                                  'r11-plate-item',
                                  { 'entity-line-entity entity-line-single-line': item.type === 'entity' }
                                ]"
                              >
                                <strong>{{ item.label }}:</strong>
                                <template v-if="item.type === 'entity'">
                                  <div class="entity-value-entity entity-value-entity-single-line">
                                    <span v-if="item.code" class="entity-code-chip" :title="item.code">{{ item.code }}</span>
                                    <span class="entity-value-name entity-value-name-single-line" :title="item.nameTitle || item.name || '-'">{{ item.name || '-' }}</span>
                                  </div>
                                </template>
                                <template v-else>
                                  <span class="r11-plate-value" :title="item.valueTitle || item.value || '-'">{{ item.value || '-' }}</span>
                                </template>
                              </div>
                              <button
                                v-if="line.action"
                                type="button"
                                class="navigate-btn r11-plate-action"
                                :class="getActionButtonClass(line.action)"
                                @click="handleLineAction(line, issue, entity)"
                              >
                                {{ getActionLabel(line.action, t) }}
                              </button>
                            </div>
                          </template>
                          <template v-else>
                            <span>{{ line.text }}</span>
                            <button
                              v-if="line.action"
                              type="button"
                              class="navigate-btn"
                              :class="getActionButtonClass(line.action)"
                              @click="handleLineAction(line, issue, entity)"
                            >
                              {{ getActionLabel(line.action, t) }}
                            </button>
                          </template>
                        </div>
                      </div>

                      <div v-if="getEntityView(issue, entity, t).actions.length" class="entity-actions">
                        <button
                          v-for="(action, actionIndex) in getEntityView(issue, entity, t).actions"
                          :key="`${issueIndex}-${entityIndex}-action-${actionIndex}`"
                          type="button"
                          class="navigate-btn"
                          :class="getActionButtonClass(action)"
                          @click="handleEntityAction(action, issue, entity)"
                        >
                          {{ getActionLabel(action, t) }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="entity-empty">
                {{ t('relatorio_entidades_afetadas_vazio') }}
              </div>
            </section>
          </article>
        </section>
      </section>

      <div v-if="!hasIssues" class="no-data-message">
        <i class="fas fa-check-circle"></i>
        <p>{{ t('relatorio_sem_analise_detalhada') }}</p>
      </div>
    </section>
  </section>
</template>
  
<script>
  import {
    getDictionary,
    getIssueKey,
    hasEntities,
    getEntitiesCount,
    getVisibleEntities,
    isR1Entity,
    isR2Entity,
    getR2EntityKey,
    isR2EntityExpanded,
    isR3Issue,
    isR3Entity,
    isR3ByTypeEntity,
    getR3EntityGroups,
    hasR3GroupedEntities,
    getR3GroupStorageKey,
    isR8Issue,
    isR8UnmetEntity,
    isR10Entity,
    isR11Entity,
    isR9Entity,
    getR8EntityGroups,
    hasR8GroupedEntities,
    getR8GroupStorageKey,
    getEntityView,
    getIssueDescription,
    stringifyEntity,
    getTagLabel,
    isActionableTag,
    getActionButtonClass,
    getActionLabel,
    formatDate,
    formatCategory,
    buildR3Tags,
    getR3EntityActions,
    getR8EntityCategory,
    getEntityKey,
    getGroupKey,
    shouldRenderEntity,
    getRealEntityIndex
  } from './relatorioIA.js';
  
  export default {
    name: 'RelatorioIssuesTab',
    props: {
      issues: { type: Array, required: true },
      problemaIgnorado: { type:Boolean },
      salvarOk: { type: Boolean, default: false },
      expandedIssues: { type: Object, default: () => ({}) },
      expandedR2Entities: { type: Object, default: () => ({}) },
      expandedR3Groups: { type: Object, default: () => ({}) },
      expandedR8Groups: { type: Object, default: () => ({}) }
    },
    computed: {
      filteredIssues() {
        return (this.issues || []).filter(issue => this.issueMatchesAba(issue));
      },
      hasIssues() {
        return this.filteredIssues.length > 0;
      }
    },
    methods: {
      t(key, params) {
        return this.$t(key, params);
      },
      isNodeIgnorado(node) {
        return !!(node && node.idIgnorado);
      },
      entityGroupsList(entity) {
        return Array.isArray(entity?.groups) ? entity.groups : [];
      },
      entityMatchesAba(issue, entity) {
        const groups = this.entityGroupsList(entity);
        if (!groups.length) {
          return this.problemaIgnorado
            ? this.isNodeIgnorado(entity)
            : !this.isNodeIgnorado(entity);
        }
        return groups.some(group => (
          this.problemaIgnorado
            ? this.isNodeIgnorado(group)
            : !this.isNodeIgnorado(group)
        ));
      },
      issueMatchesAba(issue) {
        const entities = getVisibleEntities(issue);
        if (!entities.length) {
          return this.problemaIgnorado ? this.isNodeIgnorado(issue) : !this.isNodeIgnorado(issue);
        }
        return entities.some(entity => this.entityMatchesAba(issue, entity));
      },
      visibleEntitiesForAba(issue) {
        const entities = Array.isArray(issue?.affectedEntities) ? issue.affectedEntities : [];
        return entities
          .map((entity, entityIndex) => ({ entity, entityIndex }))
          .filter(({ entity }) => shouldRenderEntity(issue, entity))
          .filter(({ entity }) => this.entityMatchesAba(issue, entity));
      },
      visibleGroupsForAba(entity) {
        const groups = this.entityGroupsList(entity);
        if (!groups.length) return [];
        return groups.filter(group => (
          this.problemaIgnorado
            ? this.isNodeIgnorado(group)
            : !this.isNodeIgnorado(group)
        ));
      },
      visibleR2LinesForAba(issue, entity, t) {
        const lines = getEntityView(issue, entity, t).lines || [];
        const groups = this.entityGroupsList(entity);
        if (!groups.length) return lines;
        return lines.filter(line => {
          if (line.type !== 'group-usage' || !Number.isInteger(line.groupIndex)) return true;
          const group = groups[line.groupIndex];
          return this.problemaIgnorado
            ? this.isNodeIgnorado(group)
            : !this.isNodeIgnorado(group);
        });
      },
      adjustProblemaIgnorado(issue) {
        this.$emit('toggle-ignorado', {
          issueKey: this.getIssueKey(issue),
          aba: this.problemaIgnorado
        });
      },
      adjustGroupingEntitiesIgnorado(issue, issueIndex, groupEntities) {
        groupEntities.forEach(entity => {
          const realIndex = getRealEntityIndex(issue, entity);
          if (realIndex >= 0) {
            this.$emit('toggle-ignorado-entity', {
              issueKey: getIssueKey(issue, issueIndex),
              entityKey: getEntityKey(issue, issueIndex, entity, realIndex),
              aba: this.problemaIgnorado
            });
          }
        });
      },
      adjustEntityIgnorado(issue, issueIndex, entity, entityIndex) {
        this.$emit('toggle-ignorado-entity', {
          issueKey: getIssueKey(issue, issueIndex),
          entityKey: getEntityKey(issue, issueIndex, entity, entityIndex),
          aba: this.problemaIgnorado
        });
      },
      adjustGroupIgnorado(issue, issueIndex, entity, entityIndex, group, groupIndex) {
        this.$emit('toggle-ignorado-group', {
          issueKey: getIssueKey(issue, issueIndex),
          entityKey: getEntityKey(issue, issueIndex, entity, entityIndex),
          groupKey: getGroupKey(entity, group, groupIndex),
          aba: this.problemaIgnorado
        });
      },
      toggleIssue(issueId, issue, index) {
        this.$emit('toggle-issue', { issueId, issue, index });
      },
      toggleR2Entity(issue, issueIndex, entity, entityIndex) {
        this.$emit('toggle-r2-entity', { issue, issueIndex, entity, entityIndex });
      },
      toggleR3Group(issue, issueIndex, groupKey) {
        this.$emit('toggle-r3-group', { issue, issueIndex, groupKey });
      },
      toggleR8Group(issue, issueIndex, groupKey) {
        this.$emit('toggle-r8-group', { issue, issueIndex, groupKey });
      },
      isIssueExpanded(issueId) {
        return !!this.expandedIssues[issueId];
      },
      isR2EntityExpanded(issue, issueIndex, entity, entityIndex) {
        const issueKey = getIssueKey(issue, issueIndex);
        const current = this.expandedR2Entities[issueKey] || null;
        if (!current) return false;
        return current === getR2EntityKey(issue, issueIndex, entity, entityIndex);
      },
      isR3GroupExpanded(issue, issueIndex, groupKey) {
        const storageKey = getR3GroupStorageKey(issue, issueIndex, groupKey);
        return !!this.expandedR3Groups[storageKey];
      },
      isR8GroupExpanded(issue, issueIndex, groupKey) {
        const storageKey = getR8GroupStorageKey(issue, issueIndex, groupKey);
        return !!this.expandedR8Groups[storageKey];
      },
      // Ações
      handleTagAction(tag, issue, entity) {
        this.$emit('tag-action', { tag, issue, entity });
      },
      handleEntityAction(action, issue, entity) {
        this.$emit('entity-action', { action, issue, entity });
      },
      handleLineAction(line, issue, entity) {
        this.$emit('line-action', { line, issue, entity });
      },
      // Delegar para funções importadas
      getDictionary,
      getIssueKey,
      hasEntities,
      getEntitiesCount,
      getVisibleEntities,
      isR1Entity,
      isR2Entity,
      getR2EntityKey,
      isR3Issue,
      isR3Entity,
      isR3ByTypeEntity,
      getR3EntityGroups,
      hasR3GroupedEntities,
      getR3GroupStorageKey,
      isR8Issue,
      isR8UnmetEntity,
      isR10Entity,
      isR11Entity,
      isR9Entity,
      getR8EntityGroups,
      hasR8GroupedEntities,
      getR8GroupStorageKey,
      getEntityView,
      getIssueDescription,
      stringifyEntity,
      getTagLabel,
      isActionableTag,
      getActionButtonClass,
      getActionLabel,
      formatDate,
      formatCategory,
      buildR3Tags,
      getR3EntityActions,
      getR8EntityCategory,
      getEntityKey,
      getGroupKey,
      shouldRenderEntity,
      getRealEntityIndex
    }
  };
</script>